/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: hooks/useChangeHistoryList.ts
 🗓️ Created: 2025-12-02
 🧩 Description:
    Hook for managing Change History list
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAuthStore } from '../../../store/auth';
import { fetchChangeHistory } from '../api/changeHistoryApi';
import { ChangeHistory, ListChangeHistoryParams, ChangeHistoryFilterValues } from '../types/changeHistory.types';

/**
 * Hook for managing Change History list
 */
export function useChangeHistoryList() {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';
  const userId = user?.id;

  // Filter state - Default to UPDATE actions only
  const [filters, setFilters] = useState<ChangeHistoryFilterValues>({
    startDate: '',
    endDate: '',
    actions: ['UPDATE'],  // Only UPDATE by default for change history
    resource: '',
    userId: '',
    domainId: '',
  });

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      if (!userId) {
        throw new Error('User ID not available');
      }

      const queryParams: ListChangeHistoryParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        // Add filters to query params
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        action: filters.actions.length > 0 ? filters.actions.join(',') : undefined,
        resource: filters.resource || undefined,
        userId: filters.userId || undefined,
        domainId: filters.domainId ? parseInt(filters.domainId, 10) : undefined,
      };

      const result = await fetchChangeHistory(queryParams, tenantSlug, userId);
      
      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('changeHistory.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, filters, tenantSlug, userId]);

  const availableColumns = useMemo(() => [
    { key: 'id', label: t('changeHistory.columns.id'), visible: false, sortable: false },
    { key: 'createdAt', label: t('changeHistory.columns.timestamp'), visible: true, sortable: true },
    { key: 'action', label: t('changeHistory.columns.action'), visible: true, sortable: true },
    { key: 'resource', label: t('changeHistory.columns.resource'), visible: true, sortable: true },
    { key: 'resourceId', label: t('changeHistory.columns.resourceId'), visible: false, sortable: false },
    { key: 'user', label: t('changeHistory.columns.user'), visible: true, sortable: false },
    { key: 'ipAddress', label: t('changeHistory.columns.ipAddress'), visible: true, sortable: false },
    { key: 'userAgent', label: t('changeHistory.columns.userAgent'), visible: false, sortable: false },
    { key: 'metadata', label: t('changeHistory.columns.metadata'), visible: true, sortable: false },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 50,
    moduleName: 'changeHistory'
  });

  // Filter handlers
  const handleFilterChange = useCallback((newFilters: ChangeHistoryFilterValues) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      actions: ['UPDATE'],  // Keep UPDATE as default
      resource: '',
      userId: '',
      domainId: '',
    });
    setTimeout(() => refresh(), 100);
  }, [refresh]);

  const handleApplyFilters = useCallback(() => {
    refresh();
  }, [refresh]);

  return {
    data: data as ChangeHistory[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    refresh
  };
}

